import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Edit2, Trash2, X, Eye, MapPin } from 'lucide-react';
import profileExtendedService from '../../services/profileExtended.service';
import toast from 'react-hot-toast';
import Button from '@components/common/Button';

const ExperienceSection = () => {
  const [experiences, setExperiences] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [viewingExperience, setViewingExperience] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    jobTitle: '',
    company: '',
    location: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
    description: ''
  });

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const response = await profileExtendedService.getExperiences();
      // Handle both response.data.data and response.data structures
      const experiencesData = response.data?.data || response.data || [];
      setExperiences(Array.isArray(experiencesData) ? experiencesData : []);
    } catch (error) {
      console.error('Failed to fetch experiences:', error);
      setExperiences([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingExperience) {
        await profileExtendedService.updateExperience(editingExperience.id, formData);
        toast.success('Experience updated successfully');
      } else {
        await profileExtendedService.addExperience(formData);
        toast.success('Experience added successfully');
      }
      
      fetchExperiences();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save experience');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (experience) => {
    setEditingExperience(experience);
    setFormData({
      jobTitle: experience.jobTitle || '',
      company: experience.company || '',
      location: experience.location || '',
      startDate: experience.startDate || '',
      endDate: experience.endDate || '',
      isCurrent: experience.isCurrent || false,
      description: experience.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (experienceId) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;

    try {
      await profileExtendedService.deleteExperience(experienceId);
      toast.success('Experience deleted successfully');
      fetchExperiences();
    } catch (error) {
      toast.error('Failed to delete experience');
    }
  };

  const resetForm = () => {
    setFormData({
      jobTitle: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
      description: ''
    });
    setEditingExperience(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex justify-between items-center mb-2.5">
        <h3 className="text-sm font-semibold text-primary-800 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary-600" />
          Experience
        </h3>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            variant="primary"
            size="sm"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-semibold text-primary-800">
              {editingExperience ? 'Edit Experience' : 'Add New Experience'}
            </h4>
            <button
              type="button"
              onClick={resetForm}
              className="p-1 text-gray-500 hover:bg-gray-200 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                placeholder="Software Engineer"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Company <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                placeholder="Company Name"
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                placeholder="City, Country"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={formData.isCurrent}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isCurrent}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    isCurrent: e.target.checked,
                    endDate: e.target.checked ? '' : formData.endDate
                  })}
                  className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-xs font-medium text-gray-700">I currently work here</span>
              </label>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white resize-y min-h-[110px]"
                placeholder="Describe your responsibilities..."
                rows="5"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              size="sm"
              loading={loading}
            >
              {editingExperience ? 'Update' : 'Add'}
            </Button>
            <Button
              type="button"
              onClick={resetForm}
              variant="outline"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto pr-1">
        {experiences.length === 0 ? (
          <div className="md:col-span-2 lg:col-span-3">
            <p className="text-gray-500 text-center py-8 text-xs">No experience added yet. Click "Add" to get started.</p>
          </div>
        ) : (
          experiences.map((exp) => (
            <div
              key={exp.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all bg-white group"
            >
              <div className="flex justify-between items-start gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-bold text-primary-900 line-clamp-1">{exp.jobTitle}</h4>
                  <p className="text-sm text-secondary-600 font-semibold line-clamp-1">{exp.company}</p>
                  {exp.location && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1 line-clamp-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {exp.location}
                    </p>
                  )}
                </div>
                <div className="flex gap-0.5 flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <Button
                    onClick={() => setViewingExperience(exp)}
                    variant="ghost"
                    className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                    title="View Details"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    onClick={() => handleEdit(exp)}
                    variant="ghost"
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    title="Edit"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(exp.id)}
                    variant="ghost"
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                <span className="text-xs font-medium text-primary-700 bg-primary-50 px-2.5 py-1 rounded-full">
                  {formatDate(exp.startDate)} - {exp.isCurrent ? 'Present' : formatDate(exp.endDate)}
                </span>
                {exp.isCurrent && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-green-50 text-green-700 rounded-full font-medium">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Current
                  </span>
                )}
              </div>

              {exp.description && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                    {exp.description}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* View Experience Modal */}
      {viewingExperience && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-base font-bold text-primary-800 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-primary-600" />
                Experience Details
              </h3>
              <button
                onClick={() => setViewingExperience(null)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4">
                <h4 className="text-xl font-bold text-primary-900 mb-1 break-words">
                  {viewingExperience.jobTitle}
                </h4>
                <p className="text-base text-secondary-600 font-semibold mb-1 break-words">
                  {viewingExperience.company}
                </p>
                {viewingExperience.location && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {viewingExperience.location}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="text-sm font-medium text-primary-700 bg-primary-100 px-3 py-1.5 rounded-full">
                  {formatDate(viewingExperience.startDate)} - {viewingExperience.isCurrent ? 'Present' : formatDate(viewingExperience.endDate)}
                </span>
                {viewingExperience.isCurrent && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-full font-semibold">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Current Position
                  </span>
                )}
              </div>

              {viewingExperience.description && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-5">
                  <h5 className="text-sm font-semibold text-primary-800 mb-2">Description</h5>
                  <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                    {viewingExperience.description}
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  onClick={() => {
                    setViewingExperience(null);
                    handleEdit(viewingExperience);
                  }}
                  variant="outline"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  type="button"
                  onClick={() => setViewingExperience(null)}
                  variant="primary"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceSection;